"use client";

import * as React from "react";
import { enUS, fr } from "date-fns/locale";
import { type Locale } from "react-day-picker";

/**
 * Map an app language tag (`en`/`fr`/`rw`/BCP-47) to a date-fns `Locale` for the date/time UI. Drives both the
 * Calendar (month/weekday names) and the locale-aware 12h/24h + AM-PM formatting in {@link TimePicker}.
 * date-fns ships no Kinyarwanda (`rw`), so it falls back to English — matching the tenant's existing behaviour.
 */
export function resolveUiDateLocale(language: string | null | undefined): Locale {
  const lang = (language ?? "").trim().toLowerCase();
  return lang === "fr" || lang.startsWith("fr-") ? fr : enUS;
}

/**
 * App-provided date-fns locale for date UI (Calendar / DatePicker). Set once near the app root via
 * {@link UiDateLocaleProvider}; the calendar components read it so month/weekday names and formatted
 * dates follow the active app language without every caller passing a `locale` prop.
 */
const UiDateLocaleContext = React.createContext<Locale | undefined>(undefined);

export function UiDateLocaleProvider({
  locale,
  children,
}: {
  locale?: Locale;
  children: React.ReactNode;
}) {
  return <UiDateLocaleContext.Provider value={locale}>{children}</UiDateLocaleContext.Provider>;
}

export function useUiDateLocale(): Locale | undefined {
  return React.useContext(UiDateLocaleContext);
}
